import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { HelpCircle, Plus, Trash2, ArrowUp, ArrowDown, Save, Eye, EyeOff } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import EmptyState from "@/components/EmptyState";

/**
 * Doctor-managed FAQ editor.
 *
 * When ANY visible FAQ exists here, the public page uses this list exclusively
 * (see backend routes_faqs.get_effective_faqs). AI-generated FAQs from the AI
 * Studio only render as a fallback when no doctor-managed FAQs exist.
 */
export default function DoctorFaqs() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState({ question: "", answer: "", is_visible: true });
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState({});

    const load = useCallback(async () => {
        try {
            const r = await api.get("/me/faqs");
            setItems(r.data.items || []);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load FAQs"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function addFaq() {
        if (!draft.question.trim() || !draft.answer.trim()) {
            toast.error("Question and answer are both required.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/me/faqs", draft);
            setDraft({ question: "", answer: "", is_visible: true });
            toast.success("FAQ added.");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Add failed"));
        } finally {
            setSaving(false);
        }
    }

    async function saveEdit(id) {
        setSaving(true);
        try {
            await api.put(`/me/faqs/${id}`, editDraft);
            setEditingId(null);
            setEditDraft({});
            toast.success("FAQ updated.");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setSaving(false);
        }
    }

    async function toggleVisible(id, current) {
        // Optimistic update — flip the visible flag immediately in UI.
        setItems((prev) => prev.map((f) => (f.id === id ? { ...f, is_visible: !current } : f)));
        try {
            await api.put(`/me/faqs/${id}`, { is_visible: !current });
            toast.success(!current ? "FAQ visible on public page" : "FAQ hidden");
        } catch (e) {
            toast.error(errMsg(e, "Toggle failed"));
            load(); // revert on failure
        }
    }

    async function deleteFaq(id) {
        if (!window.confirm("Delete this FAQ?")) return;
        try {
            await api.delete(`/me/faqs/${id}`);
            toast.success("FAQ deleted.");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Delete failed"));
        }
    }

    async function move(idx, dir) {
        const next = idx + dir;
        if (next < 0 || next >= items.length) return;
        const reordered = [...items];
        const [moved] = reordered.splice(idx, 1);
        reordered.splice(next, 0, moved);
        setItems(reordered);
        try {
            await api.post("/me/faqs/reorder", { ids: reordered.map((f) => f.id) });
        } catch (e) {
            toast.error(errMsg(e, "Reorder failed"));
            load();
        }
    }

    if (loading) return <DoctorShell><div className="text-[#17212B]/60">Loading FAQs…</div></DoctorShell>;

    return (
        <DoctorShell>
            <div className="mb-6" data-testid="faqs-header">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">Public FAQs</div>
                <h1 className="text-2xl font-bold text-[#123B5D] flex items-center gap-2">
                    <HelpCircle size={20} /> Frequently Asked Questions
                </h1>
                <p className="text-sm text-[#17212B]/60 mt-1">
                    Any FAQ you add here <b>replaces</b> the AI-generated list on your public page. Toggle visibility to hide specific entries; delete all entries to revert to AI FAQs.
                </p>
            </div>

            {/* Add new FAQ */}
            <Card className="border-[#123B5D]/10 mb-4" data-testid="faqs-add-card">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Plus size={16} className="text-[#0F766E]" /> Add a new FAQ</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                    <div>
                        <Label>Question</Label>
                        <Input
                            value={draft.question}
                            onChange={(e) => setDraft({ ...draft, question: e.target.value })}
                            placeholder="What are your consultation hours?"
                            maxLength={500}
                            data-testid="faqs-new-question"
                        />
                    </div>
                    <div>
                        <Label>Answer</Label>
                        <Textarea
                            rows={4}
                            value={draft.answer}
                            onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
                            placeholder="Monday–Friday 10am–7pm; Saturday by appointment."
                            maxLength={3000}
                            data-testid="faqs-new-answer"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch checked={draft.is_visible} onCheckedChange={(v) => setDraft({ ...draft, is_visible: !!v })} data-testid="faqs-new-visible" />
                        <span className="text-sm text-[#17212B]/70">Visible on public page</span>
                    </div>
                    <Button onClick={addFaq} disabled={saving} data-testid="faqs-add-submit">
                        <Plus size={13} /> Add FAQ
                    </Button>
                </CardContent>
            </Card>

            {/* Existing FAQs */}
            <Card className="border-[#123B5D]/10" data-testid="faqs-list-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between">
                        <span>Your FAQs ({items.length})</span>
                        {items.length === 0 ? (
                            <Badge className="bg-[#D97706]/10 text-[#D97706]">Currently using AI FAQs</Badge>
                        ) : (
                            <Badge className="bg-[#15803D]/10 text-[#15803D]">Using your FAQs</Badge>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {items.length === 0 ? (
                        <EmptyState
                            icon={HelpCircle}
                            title="No FAQs yet"
                            subtitle="Add your first question above. Your public page currently shows the AI-generated FAQ list — the first FAQ you add here will take over."
                            testId="faqs-empty"
                        />
                    ) : (
                        <ul className="divide-y">
                            {items.map((f, idx) => (
                                <li key={f.id} className="py-3" data-testid={`faqs-item-${f.id}`}>
                                    {editingId === f.id ? (
                                        <div className="space-y-2">
                                            <Input
                                                value={editDraft.question || ""}
                                                onChange={(e) => setEditDraft({ ...editDraft, question: e.target.value })}
                                                maxLength={500}
                                                data-testid={`faqs-edit-q-${f.id}`}
                                            />
                                            <Textarea
                                                rows={4}
                                                value={editDraft.answer || ""}
                                                onChange={(e) => setEditDraft({ ...editDraft, answer: e.target.value })}
                                                maxLength={3000}
                                                data-testid={`faqs-edit-a-${f.id}`}
                                            />
                                            <div className="flex gap-2">
                                                <Button size="sm" onClick={() => saveEdit(f.id)} disabled={saving} data-testid={`faqs-edit-save-${f.id}`}><Save size={13} /> Save</Button>
                                                <Button size="sm" variant="outline" onClick={() => {
                                                    const changed = editDraft.question !== f.question || editDraft.answer !== f.answer;
                                                    if (changed && !window.confirm("Discard unsaved changes to this FAQ?")) return;
                                                    setEditingId(null); setEditDraft({});
                                                }}>Cancel</Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="font-semibold text-[#123B5D] flex-1">
                                                    {f.question}
                                                    {!f.is_visible ? <span className="ml-2 text-xs text-[#D97706]">(hidden)</span> : null}
                                                </div>
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    <Button size="sm" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`faqs-up-${f.id}`}><ArrowUp size={13} /></Button>
                                                    <Button size="sm" variant="ghost" onClick={() => move(idx, +1)} disabled={idx === items.length - 1} data-testid={`faqs-down-${f.id}`}><ArrowDown size={13} /></Button>
                                                    <Button size="sm" variant="ghost" onClick={() => toggleVisible(f.id, f.is_visible)} data-testid={`faqs-toggle-${f.id}`}>
                                                        {f.is_visible ? <Eye size={13} /> : <EyeOff size={13} />}
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => { setEditingId(f.id); setEditDraft({ question: f.question, answer: f.answer }); }} data-testid={`faqs-edit-${f.id}`}>Edit</Button>
                                                    <Button size="sm" variant="ghost" onClick={() => deleteFaq(f.id)} data-testid={`faqs-delete-${f.id}`}><Trash2 size={13} /></Button>
                                                </div>
                                            </div>
                                            <p className="text-sm text-[#17212B]/70 mt-1 whitespace-pre-line">{f.answer}</p>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
